import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eventService } from '../services/db'; 
import { ref, uploadBytesResumable, getDownloadURL, listAll } from "firebase/storage";
import { storage } from '../services/firebase';
import { uploadFile } from '../services/storage';
import './EventFormPage.css';

function EventFormPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [thumbnails, setThumbnails] = useState([]);
  const [showThumbnailSelector, setShowThumbnailSelector] = useState(false);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  
  // Form state
  const [eventData, setEventData] = useState({
    name: '',
    date: '',
    time: '',
    type: 'Wedding',
    description: '',
    rsvpDeadline: '',
    isPublished: false,
    location: {
      venue: '',
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    coverImage: null
  });

  // Load thumbnails from Firebase Storage
  useEffect(() => {
    const loadThumbnails = async () => {
      try {
        setThumbnailLoading(true);
        const thumbnailsRef = ref(storage, 'thumbnail/');
        const result = await listAll(thumbnailsRef);
        
        // Check if there are items in the result
        if (result.items.length === 0) {
          console.log("No thumbnails found in Firebase storage. Using fallback thumbnails.");
          // You can add fallback thumbnails here if needed
          const fallbackThumbnails = [
            // Add URLs to any local or public thumbnail images
            { url: 'https://via.placeholder.com/300x200?text=Wedding', name: 'Wedding Thumbnail' },
            { url: 'https://via.placeholder.com/300x200?text=Birthday', name: 'Birthday Thumbnail' },
            { url: 'https://via.placeholder.com/300x200?text=Corporate', name: 'Corporate Thumbnail' },
          ];
          setThumbnails(fallbackThumbnails);
          setThumbnailLoading(false);
          return;
        }
        
        try {
          const thumbnailURLs = await Promise.all(
            result.items.map(async (item) => {
              try {
                const url = await getDownloadURL(item);
                return {
                  url,
                  name: item.name,
                  path: item.fullPath
                };
              } catch (itemError) {
                console.error(`Error getting URL for item ${item.name}:`, itemError);
                return null;
              }
            })
          );
          
          // Filter out any nulls (failed items)
          const validThumbnails = thumbnailURLs.filter(item => item !== null);
          setThumbnails(validThumbnails);
        } catch (downloadError) {
          console.error("Error getting download URLs:", downloadError);
          // Use fallback thumbnails
          setThumbnails([
            { url: 'https://via.placeholder.com/300x200?text=Wedding', name: 'Wedding Thumbnail' },
            { url: 'https://via.placeholder.com/300x200?text=Birthday', name: 'Birthday Thumbnail' },
            { url: 'https://via.placeholder.com/300x200?text=Corporate', name: 'Corporate Thumbnail' },
          ]);
        }
        
        setThumbnailLoading(false);
      } catch (error) {
        console.error("Error loading thumbnails from Firebase:", error);
        // Use fallback thumbnails
        setThumbnails([
          { url: 'https://via.placeholder.com/300x200?text=Wedding', name: 'Wedding Thumbnail' },
          { url: 'https://via.placeholder.com/300x200?text=Birthday', name: 'Birthday Thumbnail' },
          { url: 'https://via.placeholder.com/300x200?text=Corporate', name: 'Corporate Thumbnail' },
        ]);
        setThumbnailLoading(false);
      }
    };
    
    loadThumbnails();
  }, []);

  // Load event data if editing
  useEffect(() => {
    const loadEvent = async () => {
      if (eventId) {
        try {
          setLoading(true);
          console.log("Loading event with ID:", eventId, "Type:", typeof eventId);
          
          // Try to get the event with both string and number ID
          let event = null;
          try {
            // First try with original ID
            event = await eventService.getById(eventId);
            console.log("Retrieved event with original ID:", event);
          } catch (err) {
            console.error("Error with original ID:", err);
            // Try with parsed ID
            event = await eventService.getById(parseInt(eventId, 10));
            console.log("Retrieved event with parsed ID:", event);
          }
          
          if (event) {
            // Format dates for form inputs - Fix timezone issue by using local date
            const formatDateForInput = (dateString) => {
              if (!dateString) return '';
              const date = new Date(dateString);
              // Get YYYY-MM-DD format with local timezone
              return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
                .toISOString().split('T')[0];
            };
            
            const formattedDate = formatDateForInput(event.date);
            const formattedRsvpDeadline = formatDateForInput(event.rsvpDeadline);
            
            setEventData({
              ...event,
              date: formattedDate,
              rsvpDeadline: formattedRsvpDeadline
            });
            console.log("Event data set successfully");
          } else {
            console.error("No event found with ID:", eventId);
            setError("Event not found");
          }
        } catch (error) {
          console.error("Error loading event:", error);
          setError("Failed to load event data");
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadEvent();
  }, [eventId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // Handle nested properties (location data)
      const [parent, child] = name.split('.');
      setEventData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setEventData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEventData(prev => ({ ...prev, coverImage: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleThumbnailSelect = (thumbnailUrl) => {
    setEventData(prev => ({ ...prev, coverImage: thumbnailUrl }));
    setShowThumbnailSelector(false);
  };

  const handleFirebaseUpload = async (file) => {
    if (!file || !currentUser) return;
    
    try {
      const { url } = await uploadFile(file, currentUser.id);
      setEventData(prev => ({ ...prev, coverImage: url }));
    } catch (error) {
      console.error("Failed to upload image:", error);
      setError("Failed to upload image. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      if (!currentUser) {
        setError("You must be logged in to create an event");
        return;
      }
      
      if (!eventData.name.trim()) {
        setError("Event name is required");
        return;
      }
      
      // Fix timezone issue - ensure dates are stored correctly by adjusting for timezone
      const adjustDateForTimezone = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        // Add timezone offset to ensure the correct date is stored
        return new Date(date.getTime() + date.getTimezoneOffset() * 60000).toISOString();
      };
      
      // Create event object with adjusted dates
      const eventToSave = {
        ...eventData,
        date: eventData.date ? adjustDateForTimezone(eventData.date) : '',
        rsvpDeadline: eventData.rsvpDeadline ? adjustDateForTimezone(eventData.rsvpDeadline) : '',
        userId: currentUser.id,
        updatedAt: new Date().toISOString()
      };
      
      console.log("Saving event:", eventToSave);
      
      if (eventId) {
        // Update existing event - ensure ID is included and correct type
        console.log("Updating event with ID:", eventId);
        // First try with the ID as-is
        try {
          await eventService.update(eventId, {
            ...eventToSave,
            id: eventId // Ensure ID is included in the saved object
          });
        } catch (updateError) {
          console.error("Error updating with original ID:", updateError);
          // If that fails, try with parsed ID
          await eventService.update(parseInt(eventId, 10), {
            ...eventToSave,
            id: parseInt(eventId, 10) // Ensure ID is included with parsed value
          });
        }
      } else {
        // Create new event
        await eventService.create(eventToSave);
      }
      
      navigate('/events');
    } catch (error) {
      console.error("Error saving event:", error);
      setError(`Failed to save event: ${error.message || 'Please try again'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="event-form-page">
      <div className="event-form-header">
        <Link to="/events" className="back-button">
          ← Back
        </Link>
        <h1>{eventId ? 'Edit Event' : 'Create New Event'}</h1>
        <div className="form-actions">
          <button 
            className="save-draft-btn" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {eventId ? 'Update Event' : 'Create Event'}
          </button>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="event-form-content">
        <div className="form-section">
          <h2>Event Details</h2>
          
          <div className="form-group">
            <label>Event Name</label>
            <input 
              type="text" 
              name="name"
              value={eventData.name}
              onChange={handleChange}
              placeholder="Enter your event name"
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Date</label>
              <input 
                type="date" 
                name="date"
                value={eventData.date}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Time</label>
              <input 
                type="time" 
                name="time"
                value={eventData.time}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Event Type</label>
            <select 
              name="type"
              value={eventData.type}
              onChange={handleChange}
            >
              <option value="Wedding">Wedding</option>
              <option value="Engagement">Engagement</option>
              <option value="Birthday">Birthday</option>
              <option value="Anniversary">Anniversary</option>
              <option value="Other">Other</option>
            </select>
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea 
              name="description"
              value={eventData.description}
              onChange={handleChange}
              placeholder="Add details about your event"
              rows="4"
            ></textarea>
          </div>
          
          <div className="form-group">
            <label>RSVP Deadline</label>
            <input 
              type="date" 
              name="rsvpDeadline"
              value={eventData.rsvpDeadline}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="form-section">
          <h2>Location</h2>
          
          <div className="form-group">
            <label>Venue Name</label>
            <input 
              type="text" 
              name="location.venue"
              value={eventData.location.venue}
              onChange={handleChange}
              placeholder="Enter venue name"
            />
          </div>
          
          <div className="form-group">
            <label>Street Address</label>
            <input 
              type="text" 
              name="location.street"
              value={eventData.location.street}
              onChange={handleChange}
              placeholder="Enter street address"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input 
                type="text" 
                name="location.city"
                value={eventData.location.city}
                onChange={handleChange}
                placeholder="City"
              />
            </div>
            
            <div className="form-group">
              <label>State</label>
              <input 
                type="text" 
                name="location.state"
                value={eventData.location.state}
                onChange={handleChange}
                placeholder="State"
              />
            </div>
            
            <div className="form-group">
              <label>Zip Code</label>
              <input 
                type="text" 
                name="location.zipCode"
                value={eventData.location.zipCode}
                onChange={handleChange}
                placeholder="Zip Code"
              />
            </div>
          </div>
          
          <h2>Cover Image</h2>
          <div className="cover-image-section">
            {eventData.coverImage ? (
              <div className="image-preview">
                <img src={eventData.coverImage} alt="Event cover preview" />
                <button 
                  className="remove-image-btn" 
                  onClick={() => setEventData(prev => ({ ...prev, coverImage: null }))}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="cover-image-options">
                <label className="upload-label">
                  <div className="upload-placeholder">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z" fill="#ccc"/>
                      <path d="M14.5 11L11 15.5L8.5 12.5L5 17H19L14.5 11Z" fill="#ccc"/>
                    </svg>
                    <p>Upload your own image</p>
                  </div>
                  <input 
                    type="file" 
                    accept="image/*" 
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                </label>
                
                <button 
                  className="select-thumbnail-btn"
                  onClick={() => setShowThumbnailSelector(!showThumbnailSelector)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H8V4h12v12zM12 5.5v9l6-4.5z" fill="#666"/>
                  </svg>
                  <span>Select from thumbnails</span>
                </button>
              </div>
            )}

            {showThumbnailSelector && (
              <div className="thumbnail-selector">
                <h3>Select a thumbnail</h3>
                
                {thumbnailLoading ? (
                  <div className="loading-thumbnails">Loading thumbnails...</div>
                ) : thumbnails.length === 0 ? (
                  <div className="no-thumbnails">No thumbnails available</div>
                ) : (
                  <div className="thumbnails-grid">
                    {thumbnails.map((thumbnail, index) => (
                      <div 
                        key={index} 
                        className="thumbnail-item"
                        onClick={() => handleThumbnailSelect(thumbnail.url)}
                      >
                        <img src={thumbnail.url} alt={`Thumbnail ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                )}
                
                <button 
                  className="close-selector-btn"
                  onClick={() => setShowThumbnailSelector(false)}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventFormPage; 